import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WizardState } from '../types/index'

interface Props {
  state: WizardState
  updateState: (updates: Partial<WizardState>) => void
}

export function Step2({ state, updateState }: Props) {
  const { t } = useTranslation()
  const allWeekDays = t('step2.weekDays', { returnObjects: true }) as string[]
  const weekDays = [allWeekDays[1], allWeekDays[2], allWeekDays[3], allWeekDays[4], allWeekDays[5], allWeekDays[6], allWeekDays[0]]
  const weekDayIndices = [1, 2, 3, 4, 5, 6, 0]
  const [currentMonth, setCurrentMonth] = useState(new Date())

  const toggleWeekDay = (dayIndex: number) => {
    const current = state.restDays.weekDays
    const updated = current.includes(dayIndex)
      ? current.filter(d => d !== dayIndex)
      : [...current, dayIndex]
    updateState({ restDays: { ...state.restDays, weekDays: updated } })
  }

  const toggleSpecificDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0]
    const current = state.restDays.specificDates
    const exists = current.some(d => d.toISOString().split('T')[0] === dateStr)
    const updated = exists
      ? current.filter(d => d.toISOString().split('T')[0] !== dateStr)
      : [...current, date]
    updateState({ restDays: { ...state.restDays, specificDates: updated } })
  }

  const isSpecificDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0]
    return state.restDays.specificDates.some(d => d.toISOString().split('T')[0] === dateStr)
  }

  const isWeekDayRest = (date: Date) => state.restDays.weekDays.includes(date.getDay())

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    let firstDay = new Date(year, month, 1).getDay()
    firstDay = firstDay === 0 ? 6 : firstDay - 1
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days: (Date | null)[] = []
    for (let i = 0; i < firstDay; i++) days.push(null)
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i))
    return days
  }

  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })

  const allExcluded = [
    ...weekDayIndices.filter(i => state.restDays.weekDays.includes(i)).map(i => allWeekDays[i]),
    ...state.restDays.specificDates.map(d => d.getDate() + ' ' + d.toLocaleString('default', { month: 'short' }))
  ]

  return (
    <div style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
      <h1 style={{ fontSize: 32, fontWeight: 800, color: '#111827', marginBottom: 8 }}>{t('step2.title')}</h1>
      <p style={{ color: '#6B7280', marginBottom: 32, fontSize: 16 }}>{t('step2.subtitle')}</p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 32 }}>
        <span style={{ fontWeight: 500, color: !state.restDays.enabled ? '#1c3fff' : '#9CA3AF' }}>{t('step2.no')}</span>
        <button onClick={() => updateState({ restDays: { ...state.restDays, enabled: !state.restDays.enabled } })}
          style={{ position: 'relative', width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', backgroundColor: state.restDays.enabled ? '#1c3fff' : '#D1D5DB', transition: 'background-color 0.2s', outline: 'none' }}>
          <span style={{ position: 'absolute', top: 3, width: 20, height: 20, borderRadius: '50%', backgroundColor: 'white', left: state.restDays.enabled ? 25 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </button>
        <span style={{ fontWeight: 500, color: state.restDays.enabled ? '#1c3fff' : '#9CA3AF' }}>{t('step2.yes')}</span>
      </div>

      {state.restDays.enabled && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
            {weekDays.map((day, i) => {
              const dayIndex = weekDayIndices[i]
              const isSelected = state.restDays.weekDays.includes(dayIndex)
              return (
                <button key={i} onClick={() => toggleWeekDay(dayIndex)}
                  style={{ padding: '8px 14px', borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer', outline: 'none',
                    border: isSelected ? '1.5px solid #FCA5A5' : '2px solid #E5E7EB',
                    backgroundColor: isSelected ? '#FEE2E2' : 'white',
                    color: isSelected ? '#991B1B' : '#374151',
                    textDecoration: isSelected ? 'line-through' : 'none',
                    transition: 'all 0.15s' }}>
                  {day}
                </button>
              )
            })}
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: 16, border: '1px solid #E5E7EB', padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: 20, color: '#6B7280', padding: '0 8px' }}>‹</button>
              <span style={{ fontWeight: 600, color: '#111827', fontSize: 14, textTransform: 'capitalize' }}>{monthName}</span>
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: 20, color: '#6B7280', padding: '0 8px' }}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
              {['L','M','M','G','V','S','D'].map((d, i) => (
                <div key={i} style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textAlign: 'center', padding: '4px 0' }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {getDaysInMonth().map((date, i) => (
                <div key={i}>
                  {date ? (
                    <button onClick={() => toggleSpecificDate(date)}
                      style={{ width: '100%', aspectRatio: '1', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 500, outline: 'none',
                        border: isSpecificDate(date) ? '1.5px solid #FCA5A5' : isWeekDayRest(date) ? '1.5px solid #FCA5A5' : 'none',
                        backgroundColor: isSpecificDate(date) ? '#FEE2E2' : isWeekDayRest(date) ? '#FEE2E2' : 'transparent',
                        color: isSpecificDate(date) ? '#991B1B' : isWeekDayRest(date) ? '#991B1B' : '#374151',
                        textDecoration: isSpecificDate(date) || isWeekDayRest(date) ? 'line-through' : 'none',
                        transition: 'all 0.15s' }}>
                      {date.getDate()}
                    </button>
                  ) : <div />}
                </div>
              ))}
            </div>
          </div>

          {allExcluded.length > 0 && (
            <p style={{ fontSize: 13, color: '#6B7280' }}>
              {t('step2.excluded')}: {allExcluded.join(' · ')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
